import React, {ReactElement, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useDebounce} from '../../../../hooks/useDebounce';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import {IElementTextInput} from '../../../../incase-domain/elements/textInput';
import {ElementInteraction, InputElementInteractions} from '../../../../incase-domain/proptypes/runfile';
import {updateArrowsDisabledAction} from '../../../../store/actions/presentation.actions';
import {runFileElementInteractionsSelector} from '../../../../store/selectors/runfile.selectors';

const VALIDATION_ERROR_KEYS = {
    email: 'validations.email',
    numeric: 'validations.numeric',
    phone: 'validations.phone',
} as const;

interface ElementTextInputProps {
    element: IElementTextInput;
    savePresentationRunFile?: (elementRunFile: ElementInteraction, newPoints?: number) => void;
    device?: DeviceType;
    setValidated?: (error: boolean) => void;
}

function ElementTextInput(props: ElementTextInputProps): ReactElement {
    const {element, device = DeviceType.DESKTOP, savePresentationRunFile, setValidated} = props;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const debounce = useDebounce(200);

    const elementInteractions = useSelector(runFileElementInteractionsSelector);
    const slideElements = (elementInteractions && elementInteractions[element.slideId]) || {};
    const textInputElement = (slideElements[element.id] || {}) as InputElementInteractions;

    const [inputValue, setInputValue] = useState(textInputElement.inputValue);
    const [regexConfirmed, setRegexConfirmed] = useState(false);
    const [firstTry, setFirstTry] = useState(true);

    const showError = !regexConfirmed && !firstTry && !!element.regex;
    const validationErrorKey = VALIDATION_ERROR_KEYS[element.validationType as keyof typeof VALIDATION_ERROR_KEYS];
    const errorMessage = validationErrorKey ? t(validationErrorKey) : element.regexErrorMessage;

    // If input has already been answered (refreshing or coming back to the slide)
    useEffect(() => {
        if (element.regex && textInputElement?.inputValue) {
            setInputValue(textInputElement.inputValue);
            setFirstTry(false);
            setRegexConfirmed(new RegExp(element.regex).test(textInputElement.inputValue));
        }
        setValidated && setValidated(!element.regex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element.regex]);

    const handleSavePresentationRunFile = (inputValue: string, answered: boolean): void =>
        savePresentationRunFile && debounce(() => savePresentationRunFile({inputValue, answered}));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setInputValue(e.target.value);

        if (element.regex) {
            const regexChecked = new RegExp(element.regex).test(e.target.value);
            setRegexConfirmed(regexChecked);
            handleSavePresentationRunFile(e.target.value, e.target.value.length > 0 && regexChecked);
            setValidated && setValidated(regexChecked);
        } else {
            handleSavePresentationRunFile(e.target.value, e.target.value.length > 0);
        }
    };

    return (
        <label className={getElementClasses(device)} style={getElementStyle(device, element)}>
            <div className="c-inputElement__label">{element.label}</div>
            <input
                name={element.htmlName || element.label}
                type="text"
                className={`c-inputElement__input ${showError ? 'c-inputElement__input--error' : ''}`}
                placeholder={element.placeholder || 'Placeholder text'}
                value={inputValue}
                onChange={handleChange}
                onFocus={() => dispatch(updateArrowsDisabledAction(true))}
                onBlur={() => {
                    dispatch(updateArrowsDisabledAction(false));
                    if (firstTry) setFirstTry(false);
                }}
            />
            {showError && <div className="c-inputElement__error">{errorMessage}</div>}
        </label>
    );
}

function getElementStyle(device: DeviceType, element: IElementTextInput): React.CSSProperties {
    if (device === DeviceType.MOBILE) {
        return {
            fontFamily: element.fontFamily?.name,
        };
    }

    return {fontSize: `${element.font.size || 2}em`, fontFamily: element.fontFamily?.name};
}

function getElementClasses(device: DeviceType): string {
    if (device === DeviceType.MOBILE) {
        return 'c-inputElement c-inputElement--mobile';
    }

    return 'c-inputElement';
}

export default ElementTextInput;
